import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDateContext } from '../../contexts/DateContext';

const generateMonthsForward = () => {
  const monthsArray = [];
  const today = new Date();
  for (let i = 0; i < 6; i++) {
    monthsArray.push(new Date(today.getFullYear(), today.getMonth() + i, 1));
  }
  return monthsArray;
};

/** Months from newest (max) to oldest (min) so the view opens on the current month, not the start year. */
const generateMonthsInRange = (minD, maxD) => {
  const monthsArray = [];
  const start = new Date(minD.getFullYear(), minD.getMonth(), 1);
  const end = new Date(maxD.getFullYear(), maxD.getMonth(), 1);
  const cur = new Date(end);
  while (cur >= start) {
    monthsArray.push(new Date(cur));
    cur.setMonth(cur.getMonth() - 1);
  }
  return monthsArray;
};

const DatePickerModal = ({
  onClose,
  onSelect,
  initialDates = null,
  onOpenGuestSelector = null,
  singleDate = false,
  minSelectableDate = null,
  maxSelectableDate = null,
  title = null,
}) => {
  const { selectedDates: globalDates, updateDates: updateGlobalDates } = useDateContext();
  
  // Use global dates if initialDates not provided, otherwise use initialDates
  const effectiveInitialDates = initialDates || {
    checkInDate: globalDates.checkInDate,
    checkOutDate: globalDates.checkOutDate
  };
  
  const [selectedDates, setSelectedDates] = useState({
    checkIn: effectiveInitialDates?.checkInDate || null,
    checkOut: effectiveInitialDates?.checkOutDate || null
  });
  const [selectedSingleDate, setSelectedSingleDate] = useState(initialDates?.singleDate || null);

  const boundedSingleDate = Boolean(
    singleDate && minSelectableDate && maxSelectableDate
  );

  const [viewMonth, setViewMonth] = useState(() => {
    const src = initialDates?.singleDate || maxSelectableDate || new Date();
    return new Date(src.getFullYear(), src.getMonth(), 1);
  });

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const allMonths = useMemo(() => {
    if (minSelectableDate && maxSelectableDate) {
      return generateMonthsInRange(minSelectableDate, maxSelectableDate);
    }
    return generateMonthsForward();
  }, [minSelectableDate, maxSelectableDate]);

  const yearOptions = useMemo(() => {
    if (!minSelectableDate || !maxSelectableDate) return [];
    const minYear = minSelectableDate.getFullYear();
    const maxYear = maxSelectableDate.getFullYear();
    const years = [];
    for (let y = maxYear; y >= minYear; y -= 1) {
      years.push(y);
    }
    return years;
  }, [minSelectableDate, maxSelectableDate]);

  const monthRangeBounds = useMemo(() => {
    if (!minSelectableDate || !maxSelectableDate) return null;
    return {
      minYear: minSelectableDate.getFullYear(),
      minMonth: minSelectableDate.getMonth(),
      maxYear: maxSelectableDate.getFullYear(),
      maxMonth: maxSelectableDate.getMonth(),
    };
  }, [minSelectableDate, maxSelectableDate]);

  const isMonthInRange = (year, monthIndex) => {
    if (!monthRangeBounds) return true;
    const { minYear, minMonth, maxYear, maxMonth } = monthRangeBounds;
    if (year < minYear || year > maxYear) return false;
    if (year === minYear && monthIndex < minMonth) return false;
    if (year === maxYear && monthIndex > maxMonth) return false;
    return true;
  };

  const clampViewMonth = (year, monthIndex) => {
    let candidate = new Date(year, monthIndex, 1);
    if (!monthRangeBounds) return candidate;

    const minMonthStart = new Date(monthRangeBounds.minYear, monthRangeBounds.minMonth, 1);
    const maxMonthStart = new Date(monthRangeBounds.maxYear, monthRangeBounds.maxMonth, 1);
    if (candidate < minMonthStart) return minMonthStart;
    if (candidate > maxMonthStart) return maxMonthStart;
    return candidate;
  };

  const handleViewMonthChange = (year, monthIndex) => {
    setViewMonth(clampViewMonth(year, monthIndex));
  };

  const shiftViewMonth = (delta) => {
    setViewMonth((prev) => {
      const next = new Date(prev.getFullYear(), prev.getMonth() + delta, 1);
      return clampViewMonth(next.getFullYear(), next.getMonth());
    });
  };

  const canShiftViewMonth = (delta) => {
    const next = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + delta, 1);
    return isMonthInRange(next.getFullYear(), next.getMonth());
  };

  const renderCalendarGrid = (monthDate, cellClassName = '') => (
    <div className={`grid grid-cols-7 gap-[3px] py-[16px] ${cellClassName}`}>
      {getDaysInMonth(monthDate).map((date, index) => (
        <button
          key={index}
          type="button"
          onClick={() => date && !isDateDisabled(date) && handleDateClick(date)}
          disabled={!date || isDateDisabled(date)}
          className={`
            w-full h-full py-[12px] text-[14px] font-medium transition-colors
            ${!date ? 'invisible' : ''}
            ${isDateDisabled(date) ? 'text-gray-300 cursor-not-allowed' : ''}
            ${isDateSelected(date) ? 'bg-[#FF5E4C] text-white' : ''}
            ${isDateInRange(date) ? 'bg-[#FAF7F2] text-black' : ''}
            ${!isDateSelected(date) && !isDateInRange(date) && !isDateDisabled(date) ? 'text-black hover:bg-gray-50' : ''}
          `}
        >
          {date?.getDate()}
        </button>
      ))}
    </div>
  );

  // Sync with global dates when modal opens (if no initialDates provided)
  useEffect(() => {
    if (!initialDates && globalDates.checkInDate && globalDates.checkOutDate) {
      setSelectedDates({
        checkIn: globalDates.checkInDate,
        checkOut: globalDates.checkOutDate
      });
    }
  }, [initialDates, globalDates]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    // Store original body styles
    const originalStyle = window.getComputedStyle(document.body);
    const originalOverflow = originalStyle.overflow;
    const originalPosition = originalStyle.position;
    const originalTop = originalStyle.top;
    
    // Get current scroll position
    const scrollY = window.scrollY;
    
    // Apply scroll prevention
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    
    // Add CSS class for additional scroll prevention
    document.body.classList.add('modal-open');
    document.documentElement.classList.add('modal-open');
    
    // Cleanup: restore scrolling when modal closes
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = '';
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
      
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, []);

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const handleDateClick = (date) => {
    if (singleDate) {
      setSelectedSingleDate(date);
    } else {
      if (!selectedDates.checkIn || (selectedDates.checkIn && selectedDates.checkOut)) {
        setSelectedDates({ checkIn: date, checkOut: null });
      } else if (selectedDates.checkIn && !selectedDates.checkOut) {
        if (date > selectedDates.checkIn) {
          setSelectedDates({ ...selectedDates, checkOut: date });
        } else {
          setSelectedDates({ checkIn: date, checkOut: null });
        }
      }
    }
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    
    // Normalize dates to compare only date parts (ignore time)
    const normalizeDate = (dateToNormalize) => {
      const normalized = new Date(dateToNormalize);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };
    
    const normalizedDate = normalizeDate(date);
    
    if (singleDate) {
      const normalizedSingleDate = selectedSingleDate ? normalizeDate(selectedSingleDate) : null;
      return normalizedSingleDate && normalizedDate.getTime() === normalizedSingleDate.getTime();
    } else {
      const normalizedCheckIn = selectedDates.checkIn ? normalizeDate(selectedDates.checkIn) : null;
      const normalizedCheckOut = selectedDates.checkOut ? normalizeDate(selectedDates.checkOut) : null;
      
      return (normalizedCheckIn && normalizedDate.getTime() === normalizedCheckIn.getTime()) ||
             (normalizedCheckOut && normalizedDate.getTime() === normalizedCheckOut.getTime());
    }
  };

  const isDateInRange = (date) => {
    if (singleDate) return false; // No range highlighting for single date mode
    
    if (!date || !selectedDates.checkIn || !selectedDates.checkOut) return false;
    
    // Normalize dates to compare only date parts (ignore time)
    const normalizeDate = (dateToNormalize) => {
      const normalized = new Date(dateToNormalize);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };
    
    const normalizedDate = normalizeDate(date);
    const normalizedCheckIn = normalizeDate(selectedDates.checkIn);
    const normalizedCheckOut = normalizeDate(selectedDates.checkOut);
    
    return normalizedDate > normalizedCheckIn && normalizedDate < normalizedCheckOut;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    if (minSelectableDate) {
      const min = new Date(minSelectableDate);
      min.setHours(0, 0, 0, 0);
      if (d < min) return true;
    }
    if (maxSelectableDate) {
      const max = new Date(maxSelectableDate);
      max.setHours(0, 0, 0, 0);
      if (d > max) return true;
    }
    if (!minSelectableDate && !maxSelectableDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d < today;
    }
    return false;
  };

  const formatDateRange = () => {
    if (selectedDates.checkIn && selectedDates.checkOut) {
      const checkIn = selectedDates.checkIn.toLocaleDateString('en-US', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'short' 
      });
      const checkOut = selectedDates.checkOut.toLocaleDateString('en-US', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'short' 
      });
      return { checkIn, checkOut };
    }
    return null;
  };

  const formatSingleDateLabel = (date) =>
    date.toLocaleDateString('en-US', boundedSingleDate
      ? { day: 'numeric', month: 'short', year: 'numeric' }
      : { weekday: 'short', day: 'numeric', month: 'short' });

  const handleProceed = () => {
    if (singleDate) {
      if (selectedSingleDate) {
        onSelect({
          singleDate: selectedSingleDate,
          formattedDate: formatSingleDateLabel(selectedSingleDate),
        });
        onClose();
      }
    } else {
      if (selectedDates.checkIn && selectedDates.checkOut) {
        const formattedDates = {
          checkIn: selectedDates.checkIn.toLocaleDateString('en-US', { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short' 
          }),
          checkOut: selectedDates.checkOut.toLocaleDateString('en-US', { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short' 
          }),
          // Also pass the actual Date objects for proper year handling
          checkInDate: selectedDates.checkIn,
          checkOutDate: selectedDates.checkOut
        };
        
        // Update global date context
        updateGlobalDates(formattedDates);
        
        // Call the onSelect callback if provided
        if (onSelect) {
          onSelect(formattedDates);
        }
        
        // Close date picker and open guest selector if callback is provided
        onClose();
        if (onOpenGuestSelector) {
          onOpenGuestSelector();
        }
      }
    }
  };



  const headerTitle =
    title ||
    (singleDate ? 'Select date' : 'Select check in date');

  const desktopSingleClass =
    singleDate &&
    'md:w-full md:max-w-[440px] lg:max-w-[460px] md:max-h-[min(560px,92vh)]';

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[80] flex items-end md:items-center md:justify-center"
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
      onTouchMove={(e) => e.preventDefault()}
      onWheel={(e) => e.preventDefault()}
    >
      {/* Modal Container - Mobile: Slide-up from bottom, Desktop: Centered; single-date: compact card */}
      <div
        className={`flex w-full flex-col overflow-hidden rounded-t-[32px] bg-white animate-slide-up md:animate-none md:rounded-[24px] max-h-[70vh] md:max-h-[90vh] md:w-auto md:max-w-[900px] ${desktopSingleClass || ''}`}
      >
       
        {/* Header */}
        <div className='px-[16px] md:px-[32px] pt-[20px] md:pt-[24px] pb-[16px] border-b-[1px] border-[#dddddd86] bg-white'>
          <div className="flex items-center justify-between">
            <h2 className="text-[20px] md:text-[24px] font-medium text-black tracking-[-1px]">
              {headerTitle}
            </h2>
            <button 
              onClick={onClose}
              className="hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                <g clipPath="url(#clip0_2904_2203)">
                  <path d="M21 7L7 21" stroke="#4B4B4B" strokeWidth="2.33333" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 7L21 21" stroke="#4B4B4B" strokeWidth="2.33333" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_2904_2203">
                    <rect width="28" height="28" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </button>
          </div>
        </div>

        {/* Calendar Container — cap height on desktop for single-date so the dialog stays compact */}
        <div
          className={`min-h-0 flex-1 overflow-y-auto scrollbar-hide ${
            singleDate ? 'md:max-h-[340px]' : ''
          } ${boundedSingleDate ? 'max-h-[52vh]' : ''}`}
        >
          {/* Single-date mode: one scrollable calendar on all breakpoints (matches diary post UX) */}
          {/* Mobile: Single column with sticky day headers */}
          <div className={singleDate ? '' : 'md:hidden'}>
            {/* Global Days of Week Header for Mobile */}
            <div className="sticky top-0 grid grid-cols-7 gap-1 px-[16px] py-[12px] bg-white z-10 border-b-[1px] border-[#F0F2F5]">
              {daysOfWeek.map(day => (
                <div key={day} className="text-center text-[14px] leading-none font-normal text-[#08192C]">
                  {day}
                </div>
              ))}
            </div>

            {/* Months Container */}
            <div className="px-[16px]">
              {boundedSingleDate ? (
                <>
                  <div className="flex items-center justify-between gap-2 py-[16px] border-b border-[#F0F2F5]">
                    <button
                      type="button"
                      onClick={() => shiftViewMonth(-1)}
                      disabled={!canShiftViewMonth(-1)}
                      className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Previous month"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="flex items-center gap-2 flex-1 justify-center min-w-0">
                      <select
                        value={viewMonth.getMonth()}
                        onChange={(e) =>
                          handleViewMonthChange(viewMonth.getFullYear(), Number(e.target.value))
                        }
                        className="flex-1 min-w-0 max-w-[150px] px-3 py-2 text-[14px] font-medium text-black bg-white border border-[#E5E3DF] rounded-[10px] focus:outline-none focus:border-[#FF5E4C]"
                        aria-label="Select month"
                      >
                        {months.map((monthName, monthIndex) => (
                          <option
                            key={monthName}
                            value={monthIndex}
                            disabled={!isMonthInRange(viewMonth.getFullYear(), monthIndex)}
                          >
                            {monthName}
                          </option>
                        ))}
                      </select>

                      <select
                        value={viewMonth.getFullYear()}
                        onChange={(e) =>
                          handleViewMonthChange(Number(e.target.value), viewMonth.getMonth())
                        }
                        className="w-[92px] shrink-0 px-3 py-2 text-[14px] font-medium text-black bg-white border border-[#E5E3DF] rounded-[10px] focus:outline-none focus:border-[#FF5E4C]"
                        aria-label="Select year"
                      >
                        {yearOptions.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      onClick={() => shiftViewMonth(1)}
                      disabled={!canShiftViewMonth(1)}
                      className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Next month"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>

                  {renderCalendarGrid(viewMonth)}
                </>
              ) : (
                allMonths.map((monthDate, monthIndex) => (
                  <div key={monthIndex}>
                    <div className="text-center border-b-[1px] py-[16px] border-[#F0F2F5]">
                      <h3 className="text-[16px] font-medium text-black">
                        {months[monthDate.getMonth()]} {monthDate.getFullYear()}
                      </h3>
                    </div>

                    {renderCalendarGrid(monthDate)}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Desktop: Two months side by side (range mode only) */}
          <div className={singleDate ? 'hidden' : 'hidden md:block px-[32px] py-[24px]'}>
            <div className="grid grid-cols-2 gap-[48px]">
              {allMonths.slice(0, 2).map((monthDate, monthIndex) => (
                <div key={monthIndex}>
                  {/* Month Header */}
                  <div className="text-center mb-[24px]">
                    <h3 className="text-[18px] font-medium text-black">
                      {months[monthDate.getMonth()]} {monthDate.getFullYear()}
                    </h3>
                  </div>

                  {/* Days of Week Header */}
                  <div className="grid grid-cols-7 gap-[4px] mb-[12px]">
                    {daysOfWeek.map(day => (
                      <div key={day} className="text-center text-[14px] leading-none font-normal text-[#08192C]">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-[4px]">
                    {getDaysInMonth(monthDate).map((date, index) => (
                      <button
                        key={`${monthIndex}-${index}`}
                        onClick={() => date && !isDateDisabled(date) && handleDateClick(date)}
                        disabled={!date || isDateDisabled(date)}
                        className={`
                          w-[48px] h-[48px] text-[16px] font-normal transition-colors
                          ${!date ? 'invisible' : ''}
                          ${isDateDisabled(date) ? 'text-gray-300 cursor-not-allowed' : ''}
                          ${isDateSelected(date) ? 'bg-[#FF5E4C] text-white rounded-none' : ''}
                          ${isDateInRange(date) ? 'bg-[#FAF7F2] text-black' : ''}
                          ${!isDateSelected(date) && !isDateInRange(date) && !isDateDisabled(date) ? 'text-black hover:bg-gray-50' : ''}
                        `}
                      >
                        {date?.getDate()}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Summary and Action */}
        <div className="bg-white border-t border-[#dddddd86] px-[16px] md:px-[32px] py-[16px] md:py-[20px]">
          <div className="flex items-center justify-between">
            {/* Selected Date Range */}
            <div className="text-[16px] md:text-[18px] font-normal text-black">
              {singleDate ? (
                selectedSingleDate ? (
                  <div>{formatSingleDateLabel(selectedSingleDate)}</div>
                ) : (
                  <div className="text-gray-400">Select date</div>
                )
              ) : (
                formatDateRange() ? (
                  <div>
                    {formatDateRange().checkIn} - {formatDateRange().checkOut}
                  </div>
                ) : (
                  <div className="text-gray-400">Select dates</div>
                )
              )}
            </div>
            
            {/* Proceed Button */}
            <button
              onClick={handleProceed}
              disabled={singleDate ? !selectedSingleDate : (!selectedDates.checkIn || !selectedDates.checkOut)}
              className="bg-black text-white py-[12px] md:py-[14px] text-[18px] md:text-[20px] px-[50px] md:px-[80px] rounded-[12px] font-semibold hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatePickerModal; 